import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getStrength = (pwd) => {
    let score = 0;
    if (pwd.length >= 8)  score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    return score;
  };

  const strength = getStrength(password);
  const strengthLabel = ['', 'Faible', 'Moyen', 'Bon', 'Fort'][strength];
  const strengthColor = ['', '#EF4444', '#F59E0B', '#10B981', '#10B981'][strength];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirm) { setError('Les mots de passe ne correspondent pas.'); return; }
    if (strength < 2) { setError('Mot de passe trop faible.'); return; }
    if (!token) { setError('Lien invalide.'); return; }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      toast.success('Mot de passe mis à jour !');
      navigate('/connexion');
    } catch (err) {
      setError(err.response?.data?.error || 'Lien invalide ou expiré.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0D1B2A, #1A3B5D)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
          <h2>Lien invalide</h2>
          <Link to="/mot-de-passe-oublie" style={{ color: 'var(--orange)' }}>Demander un nouveau lien</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', background: 'linear-gradient(135deg, #0D1B2A 0%, #1A3B5D 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem',
    }}>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px',
          padding: '3rem', maxWidth: '460px', width: '100%',
        }}
      >
        <Link to="/connexion" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', fontSize: '0.85rem', marginBottom: '2rem' }}>
          <ArrowLeft size={16} /> Retour à la connexion
        </Link>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
          <h1 style={{ color: 'white', fontFamily: 'var(--font-display)', fontSize: '1.8rem', letterSpacing: '1px', marginBottom: '0.5rem' }}>
            NOUVEAU MOT DE PASSE
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>
            Choisissez un mot de passe sécurisé.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>

          {/* Nouveau mot de passe */}
          <div>
            <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginBottom: '0.5rem', letterSpacing: '1px' }}>
              NOUVEAU MOT DE PASSE
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Minimum 8 caractères" required
                style={{
                  width: '100%', background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px',
                  padding: '0.9rem 3rem 0.9rem 1.2rem', color: 'white',
                  fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box',
                }}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer' }}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* Indicateur de force */}
            {password && (
              <div style={{ marginTop: '0.6rem' }}>
                <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                  {[1,2,3,4].map(i => (
                    <div key={i} style={{ flex: 1, height: '4px', borderRadius: '2px', background: i <= strength ? strengthColor : 'rgba(255,255,255,0.1)', transition: 'all 0.3s' }} />
                  ))}
                </div>
                <span style={{ fontSize: '0.75rem', color: strengthColor }}>{strengthLabel}</span>
              </div>
            )}
          </div>

          {/* Confirmer mot de passe */}
          <div>
            <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginBottom: '0.5rem', letterSpacing: '1px' }}>
              CONFIRMER LE MOT DE PASSE
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirm} onChange={e => setConfirm(e.target.value)}
                placeholder="Répétez votre mot de passe" required
                style={{
                  width: '100%', background: 'rgba(255,255,255,0.08)',
                  border: `1px solid ${confirm && confirm !== password ? 'rgba(239,68,68,0.5)' : confirm && confirm === password ? 'rgba(16,185,129,0.5)' : 'rgba(255,255,255,0.15)'}`,
                  borderRadius: '10px', padding: '0.9rem 3rem 0.9rem 1.2rem',
                  color: 'white', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box',
                }}
              />
              <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer' }}>
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {confirm && confirm === password && (
              <p style={{ color: '#10B981', fontSize: '0.78rem', marginTop: '0.4rem' }}>✅ Les mots de passe correspondent</p>
            )}
          </div>

          {/* Règles */}
          <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '0.8rem 1rem' }}>
            {[
              { rule: password.length >= 8, text: '8 caractères minimum' },
              { rule: /[A-Z]/.test(password), text: 'Une majuscule' },
              { rule: /[0-9]/.test(password), text: 'Un chiffre' },
            ].map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                <span style={{ color: r.rule ? '#10B981' : 'rgba(255,255,255,0.3)', fontSize: '0.8rem' }}>{r.rule ? '✅' : '○'}</span>
                <span style={{ color: r.rule ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.3)', fontSize: '0.8rem' }}>{r.text}</span>
              </div>
            ))}
          </div>

          {error && (
            <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '0.8rem 1rem', color: '#FCA5A5', fontSize: '0.85rem' }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} style={{
            background: loading ? 'rgba(255,107,0,0.5)' : 'linear-gradient(135deg, #FF6B00, #CC4400)',
            border: 'none', color: 'white', padding: '1rem',
            borderRadius: '10px', cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.95rem',
            letterSpacing: '1.5px', boxShadow: '0 10px 30px rgba(255,107,0,0.3)',
          }}>
            {loading ? 'Mise à jour...' : 'METTRE À JOUR MON MOT DE PASSE'}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
